import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Card, CardContent } from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Switch } from '@/common/ui/switch'
import { CouponType } from '@/schema'
import { RewardType, WizardState } from './types'
import useQueryGetDepositPromotionNamesForSelectorCoupon from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionNamesForSelectorCoupon'
import { Separator } from '@/common/ui/separator'
import { Check, Ticket, Tickets, Gift, Sparkles } from 'lucide-react'

interface Step3ConfigurationProps {
	wizardState: WizardState
	onFormChange: (data: Partial<WizardState['formData']>) => void
}

const createConfigSchema = (couponType: CouponType, rewardType: RewardType) => {
	let schema = z.object({
		name: z.string().min(1, 'กรุณากรอกชื่อชุดคูปอง'),
		description: z.string().optional(),
		requireConnectedLine: z.boolean().optional(),
	})

	// Add reward-specific fields
	if (rewardType === 'promotion') {
		schema = schema.extend({
			promotionId: z.string().min(1, 'กรุณาเลือกโปรโมชั่น'),
		})
	} else {
		schema = schema.extend({
			creditAmount: z.number().positive('จำนวนเครดิตต้องมากกว่า 0'),
		})
	}

	// Add coupon-type specific fields
	if (couponType === CouponType.SINGLE) {
		schema = schema.extend({
			customCode: z
				.string()
				.min(1, 'กรุณากรอกรหัสคูปอง')
				.regex(/^\S*$/, 'รหัสคูปองต้องไม่มีช่องว่าง'),
			maxQuota: z.number().nullable().optional(),
		})
	} else {
		schema = schema.extend({
			prefix: z
				.string()
				.min(2, 'คำนำหน้าต้องมีความยาว 2-6 ตัวอักษร')
				.max(6, 'คำนำหน้าต้องมีความยาว 2-6 ตัวอักษร')
				.regex(
					/^[A-Z0-9]+$/,
					'คำนำหน้าต้องเป็นตัวอักษรภาษาอังกฤษตัวพิมพ์ใหญ่หรือตัวเลขเท่านั้น',
				),
			quantity: z.number().min(1, 'กรุณากรอกจำนวนคูปอง'),
		})
	}

	return schema
}

export default function Step3Configuration({
	wizardState,
	onFormChange,
}: Step3ConfigurationProps) {
	const { data: promotions } =
		useQueryGetDepositPromotionNamesForSelectorCoupon()

	const schema = createConfigSchema(
		wizardState.couponType!,
		wizardState.rewardType!,
	)

	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues: wizardState.formData,
		mode: 'onChange',
	})

	const isSingleType = wizardState.couponType === CouponType.SINGLE
	const isPromotion = wizardState.rewardType === 'promotion'

	// Watch all fields to update wizard state
	const watchedValues = form.watch()

	// Update wizard state on form changes
	React.useEffect(() => {
		onFormChange(watchedValues as Partial<WizardState['formData']>)
	}, [watchedValues, onFormChange])

	// Get form validation status
	const isFormValid = form.formState.isValid

	return (
		<div className="space-y-6">
			<div className="text-center">
				<h2 className="text-2xl font-bold">กรอกรายละเอียดคูปอง</h2>
				<p className="mt-2 text-muted-foreground">
					กรอกข้อมูลให้ครบถ้วนเพื่อสร้างคูปอง
				</p>
			</div>

			{/* Summary Card */}
			<Card className="border-primary">
				<CardContent className="p-4">
					<h3 className="mb-3 font-semibold">📋 สรุปการเลือก</h3>
					<div className="grid gap-3 md:grid-cols-2">
						<div className="flex items-center gap-2">
							{isSingleType ? (
								<Ticket className="h-4 w-4 text-blue-600" />
							) : (
								<Tickets className="h-4 w-4 text-purple-600" />
							)}
							<span className="text-sm">
								<span className="font-medium">ประเภท:</span>{' '}
								{isSingleType ? 'คูปองเดี่ยว' : 'คูปองหลายใบ'}
							</span>
							<Badge variant="outline">
								{isSingleType ? 'SINGLE' : 'MULTIPLE'}
							</Badge>
						</div>
						<div className="flex items-center gap-2">
							{isPromotion ? (
								<Gift className="h-4 w-4 text-orange-600" />
							) : (
								<Sparkles className="h-4 w-4 text-green-600" />
							)}
							<span className="text-sm">
								<span className="font-medium">รางวัล:</span>{' '}
								{isPromotion ? 'เชื่อมโยงโปรโมชั่น' : 'เครดิตฟรี'}
							</span>
							<Badge variant="outline">
								{isPromotion ? 'มีเงื่อนไข' : 'ไม่มีเงื่อนไข'}
							</Badge>
						</div>
					</div>
				</CardContent>
			</Card>

			<Separator />

			{/* Configuration Form */}
			<Form {...form}>
				<div className="space-y-6">
					{/* Basic Information */}
					<div className="space-y-4">
						<h3 className="font-semibold">ข้อมูลพื้นฐาน</h3>

						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										<RequiredIndicator /> ชื่อชุดคูปอง
									</FormLabel>
									<FormControl>
										<Input
											placeholder="เช่น: โปรโมชั่นวันเกิด, แจกฟรีมาแรง"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>รายละเอียด (ไม่บังคับ)</FormLabel>
									<FormControl>
										<Input
											placeholder="อธิบายเพิ่มเติมเกี่ยวกับคูปองนี้"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="requireConnectedLine"
							render={({ field }) => (
								<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<FormLabel className="text-base">
											ต้องเชื่อมต่อไลน์ก่อนใช้
										</FormLabel>
										<FormDescription>
											ผู้ใช้จะต้องเชื่อมต่อบัญชีไลน์ก่อนถึงจะใช้คูปองนี้ได้
										</FormDescription>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>
					</div>

					<Separator />

					{/* Reward Configuration */}
					<div className="space-y-4">
						<h3 className="font-semibold">การตั้งค่ารางวัล</h3>

						{isPromotion ? (
							<FormField
								control={form.control}
								name="promotionId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> เลือกโปรโมชั่น
										</FormLabel>
										<Select
											onValueChange={field.onChange}
											defaultValue={field.value}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกโปรโมชั่นที่ต้องการเชื่อมโยง" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{promotions?.data.map((promotion) => (
													<SelectItem key={promotion.id} value={promotion.id}>
														{promotion.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormDescription>
											ผู้ใช้คูปองนี้จะเข้าสู่โปรโมชั่นที่เลือกพร้อมเงื่อนไขต่างๆ
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						) : (
							<FormField
								control={form.control}
								name="creditAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> จำนวนเครดิต
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="เช่น: 50, 100, 500"
												{...field}
												value={field.value || ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : undefined,
													)
												}
											/>
										</FormControl>
										<FormDescription>
											ผู้ใช้คูปองนี้จะได้รับเครดิตเข้า Main Wallet ทันที
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
					</div>

					<Separator />

					{/* Coupon Code Configuration */}
					<div className="space-y-4">
						<h3 className="font-semibold">การตั้งค่ารหัสคูปอง</h3>

						{isSingleType ? (
							<>
								<FormField
									control={form.control}
									name="customCode"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> รหัสคูปอง
											</FormLabel>
											<FormControl>
												<Input
													placeholder="เช่น: FREE100, VIP50, LUCKY888"
													{...field}
													onChange={(e) => {
														const value = e.target.value
															.toUpperCase()
															.replace(/\s/g, '')
														field.onChange(value)
													}}
												/>
											</FormControl>
											<FormDescription>
												รหัสที่จะให้ผู้ใช้กรอก (ตัวพิมพ์ใหญ่, ไม่มีช่องว่าง)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="maxQuota"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												จำนวนครั้งสูงสุดที่สามารถใช้ได้ (โควตา)
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="ไม่ระบุ = ไม่จำกัด"
													{...field}
													value={field.value || ''}
													onChange={(e) =>
														field.onChange(
															e.target.value ? Number(e.target.value) : null,
														)
													}
												/>
											</FormControl>
											<FormDescription>
												ระบุจำนวนครั้งที่โค้ดนี้สามารถถูกใช้ได้ (เว้นว่าง =
												ไม่จำกัด)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						) : (
							<>
								<FormField
									control={form.control}
									name="prefix"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> คำนำหน้ารหัส (Prefix)
											</FormLabel>
											<FormControl>
												<Input
													placeholder="เช่น: SALE, VIP, NEW"
													{...field}
													onChange={(e) => {
														const value = e.target.value.toUpperCase()
														field.onChange(value)
													}}
													maxLength={6}
												/>
											</FormControl>
											<FormDescription>
												2-6 ตัวอักษร (A-Z, 0-9) จะสร้างโค้ดเป็น SALE-ABC123,
												SALE-XYZ789
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="quantity"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> จำนวนคูปองที่จะสร้าง
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="เช่น: 10, 50, 100, 1000"
													{...field}
													value={field.value || ''}
													onChange={(e) =>
														field.onChange(
															e.target.value
																? Number(e.target.value)
																: undefined,
														)
													}
												/>
											</FormControl>
											<FormDescription>
												จำนวนรหัสคูปองที่ต้องการสร้างในชุดนี้
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						)}
					</div>

					{/* Preview */}
					{form.watch('name') && (
						<Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
							<CardContent className="p-4">
								<div className="flex items-start gap-2">
									<Check className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-400" />
									<div className="flex-1">
										<p className="font-medium text-green-900 dark:text-green-300">
											ตัวอย่างโค้ดที่จะได้
										</p>
										<p className="mt-1 text-sm text-green-700 dark:text-green-400">
											{isSingleType
												? form.watch('customCode') || 'กรอกรหัสคูปองด้านบน'
												: `${form.watch('prefix') || 'PREFIX'}-ABC123, ${form.watch('prefix') || 'PREFIX'}-XYZ789, ...`}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			</Form>

			{!isFormValid && (
				<Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
					<CardContent className="p-4">
						<p className="text-sm text-yellow-900 dark:text-yellow-300">
							⚠️ กรุณากรอกข้อมูลให้ครบถ้วนก่อนสร้างคูปอง
						</p>
					</CardContent>
				</Card>
			)}
		</div>
	)
}
